import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Search, Library, Settings } from "lucide-react";
import Button from "./ui/Button";
import SearchBar from "./ui/SearchBar";
import { useSearchController } from "../hooks/useSearchController";
import { useAuth } from "../contexts/AuthContext";
import { useSavedBooksCount } from "../hooks/useSavedBooks";

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  onLoginClick?: () => void;
  onSignupClick?: () => void;
  onSearchSubmit?: (value: string) => void;
}

const MobileNav = ({
  open,
  onClose,
  onLoginClick,
  onSignupClick,
  onSearchSubmit,
}: MobileNavProps) => {
  const { t } = useTranslation();
  const { isAuthenticated, signOut } = useAuth();
  const { query } = useSearchController();
  const { data: savedBooksCount } = useSavedBooksCount(isAuthenticated);
  const [searchValue, setSearchValue] = useState(query);

  useEffect(() => {
    if (open) {
      setSearchValue(query);
    }
  }, [open, query]);

  if (!open) return null;

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSearchSubmit?.(searchValue);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation"
    >
      <button
        className="absolute inset-0 w-full h-full bg-transparent border-none cursor-default"
        onClick={onClose}
        onKeyDown={e => {
          if (e.key === "Escape") {
            onClose();
          }
        }}
        aria-label="Close mobile navigation"
      />
      <nav className="absolute left-0 top-0 h-full w-72 bg-background-primary p-6 shadow-lg">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="text-2xl font-bold text-orange-500" onClick={onClose}>
            chatable
          </Link>
          <Button variant="ghost" onClick={onClose} aria-label="Close menu" className="p-2">
            ✕
          </Button>
        </div>

        <form onSubmit={handleSearchSubmit} className="mb-6">
          <SearchBar
            value={searchValue}
            onChange={setSearchValue}
            onClear={() => setSearchValue("")}
            className="w-full"
          />
          <Button type="submit" className="w-full mt-3">
            {t("search.button", { defaultValue: "Search" })}
          </Button>
        </form>

        <ul className="space-y-4">
          <li>
            <Link
              to="/"
              className="flex items-center gap-3 py-2 text-lg font-medium text-text-primary hover:text-orange-500 transition-colors"
              onClick={onClose}
            >
              <Home className="w-5 h-5 text-[#BFB6AE]" />
              {t("navigation.home")}
            </Link>
          </li>
          <li>
            <Link
              to="/gallery"
              className="flex items-center gap-3 py-2 text-lg font-medium text-text-primary hover:text-orange-500 transition-colors"
              onClick={onClose}
            >
              <Search className="w-5 h-5 text-[#BFB6AE]" />
              {t("navigation.search", "Search")}
            </Link>
          </li>
          {isAuthenticated && (
            <li>
              <Link
                to="/library"
                className="flex items-center gap-3 py-2 text-lg font-medium text-text-primary hover:text-orange-500 transition-colors"
                onClick={onClose}
              >
                <Library className="w-5 h-5 text-[#BFB6AE]" />
                {t("navigation.library", "Library")}
                {savedBooksCount?.count !== undefined && savedBooksCount.count > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-[#FF6900] text-white rounded-full min-w-[20px]">
                    {savedBooksCount.count > 99 ? "99+" : savedBooksCount.count}
                  </span>
                )}
              </Link>
            </li>
          )}
          <li>
            <Link
              to="/settings"
              className="flex items-center gap-3 py-2 text-lg font-medium text-text-primary hover:text-orange-500 transition-colors"
              onClick={onClose}
            >
              <Settings className="w-5 h-5 text-[#BFB6AE]" />
              {t("navigation.settings")}
            </Link>
          </li>
        </ul>

        <div className="mt-8 space-y-3">
          {isAuthenticated ? (
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => {
                signOut();
                onClose();
              }}
            >
              {t("navigation.signOut", "Sign Out")}
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => {
                  onLoginClick?.();
                  onClose();
                }}
              >
                {t("navigation.login")}
              </Button>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => {
                  onSignupClick?.();
                  onClose();
                }}
              >
                {t("navigation.signup")}
              </Button>
            </>
          )}
        </div>
      </nav>
    </div>
  );
};

export default MobileNav;
